import React, { useState, useEffect } from 'react';
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import {
  Spin, Button, Card, Tag, message, Tooltip, Input, Modal, Space, Table
} from 'antd';
import {
  EditOutlined,
  StopOutlined,
  CheckOutlined
} from '@ant-design/icons';
import 'antd/dist/antd.css';
import Fuse from 'fuse.js';
import _ from 'lodash';
import moment from 'moment';
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux';
import * as Service from '../core/Service';
import { ActionCreators } from '../redux/actions';
import NavAndSideFrame from '../components/NavAndSideFrame';
import ADForm from '../components/ADForm';
import { displayIsActive } from '../components/table/DisplayMethod';

const title = `廣告列表`;
const selectedKey = `ad_list`;
const tableIdName = `ad_id`;
const filterableField = [
  'name',
];
const defaultFormData = {
  name: undefined,
  filepath: undefined,
  is_active: 1,
  start_date: moment().unix(),
  end_date: moment().add({day: 1}).unix(),
}

const ADList = (props) => {
  const {t} = useTranslation();

  const [dataList, setDataList] = useState([]);
  const [unChangeData, setUnChangeData] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState({});
  const [isChangingIsActive, setChangingIsActive] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getAllData();
  }, []);

  const ORIGIN = useSelector(
    (state) => state.app.origin
  );

  const getAllData = async () => {
    let endpoint = `/api/advertising/list`;
    let list = [];
    setLoading(true);
    try {
      let data = await Service.call(`GET`, endpoint);
      list = _.orderBy(data, [`ctime`], [`desc`]);
      if (data.errorMessage) list = [];
    } catch (error) {
      console.log(error);
    } finally {
      setDataList(list);
      setUnChangeData(list);
      setLoading(false);
    }
  }

  const setTableHeader = () => {
    let columns = [
      {
        title: '操作',
        dataIndex: tableIdName,
        render: (value, record) => {
          return (
            <span>
              <Tooltip title={t('common.edit')}>
                <Button
                  shape="circle"
                  style={{ marginRight: 8, color: '#000000' }}
                  icon={<EditOutlined />}
                  onClick={() => {
                    setSelectedRecord(record);
                    setModalVisible(true);
                  }}
                />
              </Tooltip>
              <Tooltip title={record.is_active ? t('ad.disable_ad') : t('ad.enable_ad')}>
                <Button
                  shape="circle"
                  style={{ color: record.is_active ? 'red' : 'green' }}
                  icon={record.is_active ? (<StopOutlined />) : (<CheckOutlined />)}
                  onClick={async () => {
                    if (isChangingIsActive) return; // avoid multiple attempts

                    setChangingIsActive(true);
                    let { ad_id, is_active } = record;
                    is_active = !is_active;

                    let result = await Service.call('put', `/api/advertising`, { ad_id, is_active });
                    setChangingIsActive(false);

                    if (_.isEmpty(result) || result.status < 1) {
                      return message.error(is_active ? t('ad.enable_failed') : t('ad.disable_failed'));
                    }
                    await getAllData();
                    return message.success(is_active ? t('ad.enabled_ad') : t('ad.disabled_ad'));
                  }}
                />
              </Tooltip>
            </span>
          )
        }
      },
      { title: t('ad.ad_id'), dataIndex: tableIdName },
      { title: t('ad.ad_priority'), dataIndex: `priority` },
      {
        title: t('common.is_active'),
        dataIndex: 'is_active',
        render: (value) => {
          return displayIsActive(value, t);
        },
        ellipsis: true
      },
      { title: t('ad.ad_name'), dataIndex: `name` },
      { title: t('ad.ad_file'), 
        dataIndex: `filepath`, 
        render: (value) => {
          return <img alt='file' src={`${ORIGIN}/${value}`} style={{height: 40}} />
        }
      },
      {
        title: t('common.start_time'),
        dataIndex: `start_date`,
        render: (value) => {
          return displayMoment(value);
        },
        ellipsis: true,
      },
      {
        title: t('common.end_time'),
        dataIndex: `end_date`,
        render: (value) => {
          return displayMoment(value);
        },
        ellipsis: true,
      },
      {
        title: t('common.ctime'),
        dataIndex: `ctime`,
        render: (value) => {
          return displayMoment(value);
        },
        ellipsis: true,
      },
      {
        title: t('common.utime'),
        dataIndex: `utime`,
        render: (value) => {
          return displayMoment(value);
        },
        ellipsis: true,
      },

    ];
    return columns;
  }

  const displayMoment = (unixTime, outputFormat = 'YYYY/MM/DD HH:mm') => {
    if (unixTime !== 0) {
      let displayTime = moment.unix(unixTime).format(outputFormat);
      return displayTime;
    }
    return '-';
  }

  const getDataBySearchKeyword = (keywords) => {
    let dataList = [];
    try {
      const options = {
        includeScore: true,
        findAllMatches: true,
        threshold: 0.15,
        keys: filterableField
      }
      const fuse = new Fuse(unChangeData, options)
      const result = fuse.search(keywords);
      console.log("RESULT", result);
      dataList = _.map(result, 'item');
      dataList = _.orderBy(dataList, [`ctime`], [`desc`]);
    } catch (error) {
      console.error('error >>> ', error);
    } finally {
      setDataList(dataList);
    }
  }

  let timeout;
  const debounce = (func, delay) => {
    clearTimeout(timeout);
    timeout = setTimeout(func, delay);
  };

  const onChange = (keyword) => {
    debounce(() => {
      getDataBySearchKeyword(keyword);
      if (_.isEmpty(keyword)) setDataList(unChangeData);
    }, 400);
  };

  return (
    <NavAndSideFrame {...props} title={title} selectedKey={selectedKey}>
      <Card>
        <Button
          icon={<EditOutlined />}
          style={{ marginRight: 10 }}
          type="primary"
          onClick={() => {
            setSelectedRecord(defaultFormData);
            setModalVisible(true);
          }}
        >
          新增
        </Button>

        <Space direction="horizontal">
          <>以文字搜尋:</>
          <Input
            placeholder="請輸入文字"
            onChange={(values) => {
              onChange(values.target.value);
            }}
          />
        </Space>
      </Card>

      <Card>
        <Spin spinning={loading}>
          <Table
            pagination={{
              defaultPageSize: 25,
              showSizeChanger: true,
              pageSizeOptions: [25, 50, 100, 200, 500]
            }}

            scroll={{ x: `max-content` }}
            dataSource={dataList}
            columns={setTableHeader()}
            rowKey={tableIdName}
          />
        </Spin>
      </Card>
      <Modal
        title={t('ad.ad')}
        visible={modalVisible}
        footer={null}
        style={{ maxWidth: 800 }}
        width="90%"
        onCancel={() => { setModalVisible(false) }}
        destroyOnClose
      >
        <ADForm
          selectedRecord={selectedRecord}
          openModal={(value) => {
            getAllData();
            setModalVisible(value);
          }}
        />
      </Modal>

    </NavAndSideFrame>
  );
}

// export default VehicleList;

function mapStateToProps(state) {
  return {
    app: state.app
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(ActionCreators, dispatch);
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ADList);